/**
 * Run calculation — /settings/calculations/$key/run
 *
 * Standalone run page. Renders a form matching the calculation's
 * declared inputs, submits to the `run` endpoint, shows the result
 * and execution trace. Used for manual debugging and for validating
 * a calculation before wiring it into a workflow or layout widget.
 */

import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Play } from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/calculations/$key/run')({
  component: RunCalculationPage,
})

function RunCalculationPage() {
  const { key } = Route.useParams()

  const { data: calc, isLoading } = useQuery(
    orpc.extensions.calculations.get.queryOptions({ input: { key } }),
  )

  const [inputs, setInputs] = useState<Record<string, unknown>>({})
  const [result, setResult] = useState<{
    status: 'success' | 'failed' | 'timeout'
    output?: unknown
    error?: string
    trace: Array<{
      statementIndex: number
      bindingName: string
      result: unknown
      evaluatedAt: string
    }>
  } | null>(null)

  const runMutation = useMutation(
    orpc.extensions.calculations.run.mutationOptions({
      onSuccess: (r) => setResult(r),
      onError: (err) => {
        setResult({
          status: 'failed',
          error: err.message,
          trace: [],
        })
      },
    }),
  )

  if (isLoading || !calc) {
    return <Skeleton className="h-96 w-full" />
  }

  const handleRun = () => {
    runMutation.mutate({ key, inputs })
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl space-y-4">
      <Link
        to="/settings/calculations/$key"
        params={{ key }}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to editor
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>Run: {calc.name}</CardTitle>
          <p className="text-sm text-muted-foreground">{calc.description}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {calc.inputs.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              This calculation takes no inputs. Click Run to execute it.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {calc.inputs.map((inp) => (
                <InputField
                  key={inp.key}
                  input={inp}
                  value={inputs[inp.key]}
                  onChange={(v) => setInputs((prev) => ({ ...prev, [inp.key]: v }))}
                />
              ))}
            </div>
          )}
          <div className="flex justify-end pt-4 border-t">
            <Button type="button" onClick={handleRun} disabled={runMutation.isPending}>
              <Play className="h-4 w-4 mr-2" />
              {runMutation.isPending ? 'Running…' : 'Run'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardHeader className="flex flex-row items-center gap-2">
            <CardTitle className="text-base">Result</CardTitle>
            <Badge
              variant={
                result.status === 'success'
                  ? 'default'
                  : result.status === 'timeout'
                    ? 'secondary'
                    : 'destructive'
              }
            >
              {result.status}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-3">
            {result.error && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                {result.error}
              </div>
            )}
            {result.output !== undefined && result.output !== null && (
              <div>
                <Label className="text-xs">Output</Label>
                <pre className="bg-muted rounded p-3 text-sm font-mono overflow-x-auto">
                  {JSON.stringify(result.output, null, 2)}
                </pre>
              </div>
            )}
            {result.trace.length > 0 && (
              <div>
                <Label className="text-xs">Execution trace</Label>
                <div className="space-y-1 mt-2">
                  {result.trace.map((step, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-2 text-xs font-mono bg-muted/40 p-2 rounded"
                    >
                      <span className="text-muted-foreground">{i + 1}.</span>
                      <div className="flex-1 min-w-0">
                        <div>
                          <span className="font-semibold">{step.bindingName}</span> ={' '}
                          <span>{JSON.stringify(step.result)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function InputField({
  input,
  value,
  onChange,
}: {
  input: {
    key: string
    label: string
    type: string
    required?: boolean
    unit?: string | null
    helpText?: string
  }
  value: unknown
  onChange: (v: unknown) => void
}) {
  const id = `run-input-${input.key}`
  const label = (
    <Label htmlFor={id} className="text-xs">
      {input.label}
      {input.required && <span className="text-destructive"> *</span>}
      {input.unit && (
        <span className="text-muted-foreground ml-1">({input.unit})</span>
      )}
    </Label>
  )
  const help = input.helpText && (
    <p className="text-xs text-muted-foreground mt-1">{input.helpText}</p>
  )

  if (input.type === 'boolean') {
    return (
      <div>
        {label}
        <div className="flex items-center gap-2 mt-1">
          <input
            id={id}
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
          />
        </div>
        {help}
      </div>
    )
  }

  if (input.type === 'number' || input.type === 'currency') {
    return (
      <div>
        {label}
        <Input
          id={id}
          type="number"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => {
            const v = e.target.value === '' ? null : Number(e.target.value)
            onChange(Number.isNaN(v) ? null : v)
          }}
        />
        {help}
      </div>
    )
  }

  if (input.type === 'date') {
    return (
      <div>
        {label}
        <Input
          id={id}
          type="date"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
        />
        {help}
      </div>
    )
  }

  // text, record id, etc.
  return (
    <div>
      {label}
      <Input
        id={id}
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={input.type === 'record' ? 'record id' : undefined}
      />
      {help}
    </div>
  )
}
